import {
  Mutation,
  PinwheelLinkEventResultTypenames,
} from '@varo/graphql-schema-types';
import { logBreadcrumb, logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { OperationResult } from 'urql';

const SENTRY_CONTEXT = {
  featureArea: FeatureArea.PAYMENTS,
  responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
  severity: ErrorSeverity.MEDIUM,
};

// not raced against a timeout: the urql mutation promise always settles (success, GraphQL
// error, or network error) via the client's own request handling, so a client-side timeout
// here would be redundant and risks abandoning a save that would have succeeded a moment later
export const confirmSuccessLinkEventSaved = async (
  linkEventResult: Promise<Pick<OperationResult<Mutation>, 'data' | 'error'>>,
): Promise<void> => {
  try {
    const result = await linkEventResult;
    const linkEvent = result.data?.pinwheelLinkEvent;

    if (
      linkEvent?.__typename ===
      PinwheelLinkEventResultTypenames.PinwheelLinkEventSuccess
    ) {
      return;
    }

    if (result.error?.networkError) {
      logBreadcrumb({
        level: 'warning',
        message: `Pinwheel success link event save was not confirmed: ${result.error.networkError.message}`,
      });
      return;
    }

    const linkEventError =
      linkEvent?.__typename ===
      PinwheelLinkEventResultTypenames.PinwheelLinkEventError
        ? { code: linkEvent.code, message: linkEvent.message }
        : undefined;

    logErrorWithContext(
      new Error('Pinwheel success link event was not confirmed by the server'),
      {
        ...SENTRY_CONTEXT,
        additionalContext: {
          linkEventError,
          transportError: result.error?.message,
        },
      },
    );
  } catch (mutationError) {
    logErrorWithContext(
      mutationError instanceof Error
        ? mutationError
        : new Error('Pinwheel success link event mutation failed'),
      SENTRY_CONTEXT,
    );
  }
};
